<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div
      v-if="accountStatusUpdateSuccessCount.length || accountStatusUpdateFailed.length"
      class="m-2"
    >
      <b-row>
        <b-col
          v-if="accountStatusUpdateSuccessCount.length"
          md="6"
          class="mb-2"
        >
          <b-alert
            show
            variant="primary"
            class="p-2"
          >
            {{ accountStatusUpdateSuccessCount }}
          </b-alert>
        </b-col>
        <b-col
          v-if="accountStatusUpdateFailed.length"
          md="6"
          class="mb-2"
        >
          <div>
            <b-button variant="danger">
              {{ $t('FAILED LISTS') }}
            </b-button>
          </div>
          <b-table
            ref="accountStatusUpdateFailed"
            :items="accountStatusUpdateFailed"
            responsive
            :fields="failedHeaders"
            primary-key="id"
            show-empty
            :empty-text="$t('No records available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          /></b-col>
      </b-row>
    </div>

    <div class="m-2">
      <b-row class="d-flex align-items-center">
        <b-col md="5">
          <validation-observer ref="batchAccountStatusUpdateRules">
            <b-form>
              <b-col
                md="12"
                class="mb-2"
              >
                <div class="mb-1">
                  {{ $t('Select file (.xlsx format only)') }}
                </div>
                <b-form-file
                  id="file-name"
                  v-model="hasFileData"
                  :label="$t('Select file (.xlsx format only)')"
                  :state="Boolean(hasFileData)"
                  :placeholder="$t('Choose a file or drop it here...')"
                  :drop-placeholder="$t('Drop file here...')"
                  :error-name="$t('file')"
                  rules="required"
                  accept=".xlsx"
                  @change="onChangeXLSX($event)"
                />
              </b-col>
            </b-form>
          </validation-observer>
        </b-col>

        <b-col
          md="7"
        >
          <b-row>
            <b-col
              md="4"
              class="mt-2"
            >
              <SwitchButton
                v-model="enablePreview"
                :label="$t('Enable Preview?')"
                name="enable-preview"
              />
            </b-col>
            <b-col md="3">
              <Button
                v-if="getUserPermission('BATCH_ACCOUNT_UPDATE')"
                :label="$t('Create Account Status')"
                size="md"
                :loading="isLoading"
                :disabled="isDisabled"
                @click="handleSubmit"
              />
            </b-col>
            <b-col md="5">
              <Button
                class="ml-lg-2"
                align="end"
                :label="$t('Download Batch Account Status Update Format')"
                size="md"
                icon="DownloadIcon"
                show-icon
                :loading="isLoading2"
                :disabled="isDisabled2"
                @click="handleDownloadSubmit"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <b-table
      v-if="errorMessages.length"
      ref="accountStatusUpdateSuccessCountReport"
      :items="errorMessages"
      responsive
      :fields="translatedErrorHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    />
    <p
      v-if="totalSheetRows && !enablePreview"
      class="p-2"
      style="font-size: 18px;"
    >
      {{ $t('There are total of') }} ({{ totalSheetRows }}) {{ $t('Records fetched') }}
    </p>
    <b-table
      v-if="enablePreview"
      ref="glCustomerPostingRef"
      :items="parseExcelFile"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      name="table-pagination"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BTable, BFormFile, BButton, BAlert,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import XLSX from 'xlsx'
import Button from '../../components/button/Button.vue'
import SwitchButton from '../../components/switch/Switch.vue'
import { batchAccountFormat, tableLessHeaders, tableAllHeaders } from '../../../helpers/data'
import TablePagination from '@/views/components/table/TablePagination.vue'

export default {
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BForm,
    BAlert,
    BFormFile,
    ValidationObserver,
    Button,
    BButton,
    SwitchButton,
    TablePagination,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      enablePreview: false,
      totalSheetRows: '',
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      validatedExcelFileData: [],
      sortOrders: {},
      accountFormat: batchAccountFormat,
      successHeaders: tableAllHeaders,
      failedHeaders: tableLessHeaders,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'CASA Number' },
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'accountStatus', sortable: true, label: 'Account Status' },
        { key: 'phoneNumber', sortable: true, label: 'Phone Number' },
        { key: 'email', sortable: true, label: 'Email Address' },
        { key: 'gender', sortable: true, label: 'Gender' },
        { key: 'bvn', sortable: true, label: 'BVN' },
        { key: 'nin', sortable: true, label: 'NIN' },
        { key: 'employeeID', sortable: true, label: 'Employee ID' },
        { key: 'address', sortable: true, label: 'Address' },
      ],
      required,
      isDisabled: false,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,
      isCreateBatchHidden: true,
      accountStatusUpdateSuccessCount: [],
      accountStatusUpdateFailed: [],
      sortDesc: false,
      sortBy: 'lastName',
      errorHeaders: [
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'message', sortable: true, label: 'Error Messages' },
      ],
      errorMessages: [],
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedErrorHeaders() {
      return this.errorHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  methods: {
    ...mapActions({
      batchAccountStatusUpdate: 'batch/batchAccountStatusUpdate',
      downloadAccountFormat: 'batch/downloadAccountFormat',
    }),
    async handleSubmit() {
      this.$refs.batchAccountStatusUpdateRules.validate().then(success => {
        if (success) {
          this.confirm(this.batchAccountStatusUpdateAction, { text: this.$t('Are you sure you want to Create Batch Account Status Update?') })
        }
      })
    },
    async batchAccountStatusUpdateAction() {
      try {
        this.accountStatusUpdateSuccessCount = ''
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          accountStatus: item.accountStatus ? Number(item.accountStatus) : null,
        }))
        const response = await this.batchAccountStatusUpdate({ payload: formData })
        const { status, data } = response
        if (status === 200) {
          this.hasFileData = []
          this.parseExcelFile = []
          if (data.successCount > 1) {
            const accountStatusText = this.$t('Accounts Status Update was successfully created')
            this.accountStatusUpdateSuccessCount = `(${data.successCount}) ${accountStatusText}`
          } else {
            const accountStaText = this.$t('Account Status Update was successfully created')
            this.accountStatusUpdateSuccessCount = `(${data.successCount}) ${accountStaText}`
          }
          this.accountStatusUpdateFailed = data.failed

          this.isDisabled = false
          this.isLoading = false
          this.totalSheetRows = ''
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const reasonText = this.$t('Your request to create Batch Account Status Update failed. Reason')
          this.alertError({ text: `${reasonText}: ${responseError.errorMessage}` })
        })
        this.isDisabled = false
        this.isLoading = false
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    onChangeXLSX(event) {
      this.errorMessages = []
      this.isCreateBatchHidden = true
      this.fileData = event.target.files ? event.target.files[0] : null
      if (this.fileData) {
        const vm = this
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary', cellDates: true, dateNF: 'mm/dd/yyyy;@' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rowObject = XLSX.utils.sheet_to_row_object_array(worksheet, { raw: false })
          vm.totalSheetRows = rowObject.length
          vm.parseExcelFile = JSON.parse(JSON.stringify(rowObject)).map(items => ({
            accountNumber: items['CASA Number'],
            referenceNumber: items['Reference Number'],
            accountStatus: items['Account Status'],
            phoneNumber: items['Phone Number'],
            email: items['Email Address'],
            gender: items.Gender ? Number(items.Gender) : null,
            bvn: items.BVN,
            nin: items.NIN,
            employeeID: items['Employee ID'],
            address: items.Address,
          }))
        }
        reader.readAsArrayBuffer(this.fileData)
      }
    },
    async handleDownloadSubmit() {
      this.confirm(this.downloadAction, { text: this.$t('Are you sure you want to download Batch Account Status Update Format?') })
    },
    async downloadAction() {
      try {
        this.isLoading2 = true
        this.isDisabled2 = true
        const ACCOUNT_STATUS_UPDATE_TEMPLATE = 24
        const response = await this.downloadAccountFormat(ACCOUNT_STATUS_UPDATE_TEMPLATE)
        if (response.status === 200) {
          const downloadAnchor = document.createElement('a')
          downloadAnchor.setAttribute('href', response.data.data)
          const filename = 'Udara360-Batch-Account-Status-Update.xlsx'
          downloadAnchor.setAttribute('download', filename)
          document.body.appendChild(downloadAnchor)
          downloadAnchor.click()
          document.body.removeChild(downloadAnchor)
          this.alertSuccess({ text: this.$t('Your request to download Batch Account Status Update Format was successful') })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const reasonText = this.$t('Your request to download Batch Account Status Update Format failed. Reason')
          this.alertError({ text: `${reasonText}: ${responseError.errorMessage}` })
          this.isLoading2 = false
          this.isDisabled2 = false
        })
      } finally {
        this.isLoading2 = false
        this.isDisabled2 = false
      }
    },
  },
}
</script>
